import React from "react";
import { Box, Button, FormHelperText, Grid, IconButton, Paper, TextField } from "@mui/material";
import styled from "styled-components";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import Div from "../../components/div";
import Flex from "../../components/flex";
import request from "../../utils/request";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0px;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0px;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.text.secondary};
    cursor: pointer;
`;

const ResponseText = styled(FormHelperText)`
    color: ${({ theme, error }) => (error ? theme.palette.error.main : theme.palette.primary.main)};
`;

const InviteCard = ({ invitesApi }) => {
    const [emails, setEmails] = React.useState([{ value: undefined }]);
    const [response, setResponse] = React.useState();

    const addEmail = () => setEmails([...emails, { value: undefined }]);

    const removeEmail = () => setEmails(emails.slice(0, -1));

    const clearError = (index) => {
        const newEmails = [...emails];
        newEmails[index].error = undefined;
        setEmails(newEmails);
        if (response) setResponse(undefined);
    };

    const changeValue = (event, index) => {
        const newEmails = [...emails];
        newEmails[index].value = event.target.value;
        setEmails(newEmails);
        if (emails[index].error) clearError(index);
    };

    const checkValue = (index) => {
        const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!re.test(emails[index].value)) {
            const newEmails = [...emails];
            newEmails[index].error = "Please enter a valid email address";
            setEmails(newEmails);
        }
    };

    const onFormSubmit = async (e) => {
        e.preventDefault();
        emails.forEach((email, index) => checkValue(index));
        const emailErrors = emails.filter((email) => email.error);
        if (emailErrors.length > 0) return;
        try {
            const invites = emails.map((email) =>
                request("/api/invites/plg-invite", {
                    method: "POST",
                    body: JSON.stringify({
                        email: email.value,
                        targetUnit: "*",
                        permissions: ["super-admin"],
                    }),
                })
            );
            await Promise.all(invites);
            invitesApi.refetch();
            setResponse({ message: `Users successfully invited` });
        } catch (error) {
            setResponse({ message: `There was an error inviting the users`, error: true });
        }
    };

    return (
        <Paper
            style={{
                width: "100%",
                maxWidth: "850px",
                borderRadius: "14px",
            }}
        >
            <Box my={3} mx={4} pb={3}>
                <form onSubmit={onFormSubmit}>
                    <Grid container spacing={3} direction="column">
                        <Grid item>
                            <Div>
                                <H1>Invite to Team</H1>
                                <H2>Add the emails of users you would like to add to work team</H2>
                            </Div>
                        </Grid>
                        {emails.map((email, index) => (
                            <Grid
                                container
                                item
                                spacing={2}
                                justifyContent="space-between"
                                key={email}
                            >
                                <Grid item style={{ width: "100%" }}>
                                    <TextField
                                        error={!!email.error}
                                        id={`email-${index}`}
                                        label="Email"
                                        value={email.value || ""}
                                        helperText={email.error}
                                        variant="outlined"
                                        onChange={(event) => changeValue(event, index)}
                                        onBlur={() => checkValue(index)}
                                        onFocus={() => clearError(index)}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        ))}
                        <Grid item>
                            <Grid container item spacing={0} direction="row">
                                <Grid item>
                                    <IconButton
                                        id="add-email"
                                        color="primary"
                                        onClick={addEmail}
                                        style={{ marginLeft: "-14px" }}
                                        size="large"
                                    >
                                        <AddCircle />
                                    </IconButton>
                                </Grid>
                                <Grid item alignItems="center">
                                    <Flex height="100%">
                                        <Label htmlFor="add-email">Add another invite</Label>
                                    </Flex>
                                </Grid>
                            </Grid>
                            {emails.length > 1 && (
                                <Grid container item spacing={0} direction="row">
                                    <Grid item>
                                        <IconButton
                                            id="remove-email"
                                            color="warning"
                                            onClick={removeEmail}
                                            style={{ marginLeft: "-14px" }}
                                            size="large"
                                        >
                                            <RemoveCircle />
                                        </IconButton>
                                    </Grid>
                                    <Grid item alignItems="center">
                                        <Flex height="100%">
                                            <Label htmlFor="remove-email">Remove last invite</Label>
                                        </Flex>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="primary" type="submit">
                                Send Invites
                            </Button>
                            {response && (
                                <ResponseText error={response.error} color="primary">
                                    {response.message}
                                </ResponseText>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Paper>
    );
};

export default InviteCard;
